import * as storage from '../../utils/storage';
import { client } from '../../config/apollo/client';
import { gql } from '@apollo/client';

export const GET_INSTANT_HELP_VIDEO = gql`
query getInstantHelpVideo($input:InstantHelpVideoChatInput!) {
  getInstantHelpVideoDetails(input:$input) {
    id
    connectWith {
      openTokKey
      openTokToken
      openTokSessionId
    }
  }
}
`;

export const GET_REGION = gql`
query region($criteria: RegionCriteria!) {
  regions(criteria: $criteria) {
    items {
      _id 
      freePhoneNumber
      dialPhoneNumber
      phoneLanguages
      localLanguagePhoneStartingHour
      localLanguagePhoneEndHour
      officeHourStartingHour
      officeHourEndHour
      regionCode
      utcTimezone
      videocallLocalLanguage
      localLanguageAvailable
      beginningOfTheWeek
      endOfTheWeek
    }
  }
}
`;

export const GET_INSTANT_HELP_PHONE = gql`
query getRegions($region:CountryKey) {
	regions(criteria:{filters:{regionCode:{value:$region, operation:EQ}}}) {
    items {
      phoneLanguages
      freePhoneNumber
      dialPhoneNumber
      regionCode
    }
    meta {
      total 
    }
  }
}
`;

export const UPDATE_REQUEST_STATUS = gql`
query wpoRequest(
  $status:statusUpdateRequest!,
  $requestType:requestType!,
  $requestId:Float!,
) {
  updateWPORequest(
    status:$status,
    requestType:$requestType,
    requestId:$requestId,
  ) {
    status
    message
  }
}
`;

export const queryUpdateWpoRequest = async(
  requestId: number,
  status: 'EXPIRED' | 'CANCELLED' | 'ANY',
  requestType: 'IM' | 'VIDEO' = 'VIDEO',
) => {
  // updateWPORequest is permission-gated behind GetInstantHelpVideo, and the schema is
  // filtered per caller, so with no token the field does not exist at all and the server
  // answers 'Cannot query field "updateWPORequest" on type "Query"'. That is what shows
  // up after logging out: authLink sends `authorization: ''` the moment the token is
  // removed, and any in-flight cancel/expire lands anonymous.
  //
  // Firing it anyway cannot succeed, so skip and say so plainly -- the misleading schema
  // error hid the part that actually matters, which is that the WPO request stays OPEN.
  // An open request is what later makes WPO answer "a call is already going on".
  const token = await storage.loadString('token');
  if (!token) {
    console.warn(
      `[wpoRequest] skipped: no session. WPO request ${requestId} (${requestType}) was ` +
      `left OPEN instead of being set to ${status}; it must be closed before a new call.`
    );
    return { loading: false, data: undefined };
  }

  const response = await client.query({
    query: UPDATE_REQUEST_STATUS,
    variables: {
      requestId,
      status,
      requestType,
    },
    fetchPolicy: 'no-cache',
  });
  if (response.error) {
    console.error(response.error);
  }
  if (response.errors) {
    console.error(response.errors);
  }
  return {
    loading: response?.loading,
    data: response?.data,
  };
};

export const CLOSE_ACTIVE_VIDEO_REQUEST = gql`
query closeActiveWpoVideoRequest {
  closeActiveWpoVideoRequest {
    closed
    reason
    requestId
  }
}
`;

/**
 * Closes this user's open WPO video request, if any. Takes no id: the server asks WPO
 * which request is active for this user, so a stale one cannot be sent.
 *
 * Called at logout while the token is still valid -- that ordering is the whole point.
 * Never throws and never blocks logout: a user without WPO access does not even have
 * this field in their filtered schema, and WPO itself can be slow or down.
 */
export const queryCloseActiveWpoVideoRequest = async(timeoutMs = 5000) => {
  // logout runs more than once: the screen's own call, and then again from
  // useSilentLogin once the session is gone. The second pass has no token, so this
  // field is not in its filtered schema and the server answers 'Cannot query field
  // "closeActiveWpoVideoRequest"'. Nothing is open to close by then anyway -- the
  // first pass already did it while the session was still valid.
  const token = await storage.loadString('token');
  if (!token) {
    return;
  }
  // The catch is attached HERE, when the promise is created, not via the await below.
  // If the timeout wins the race the query is still in flight, and a rejection arriving
  // afterwards would have nothing attached to it -- an unhandled rejection, which is the
  // exact failure that used to break logout. Settling it to an outcome makes a late
  // failure harmless whoever wins.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const query = client
    .query({ query: CLOSE_ACTIVE_VIDEO_REQUEST, fetchPolicy: 'no-cache' })
    .then(
      response => ({ kind: 'ok' as const, response }),
      // Covers WPO or the API being down or erroring, and the case where the caller
      // lacks GetInstantHelpVideo so the field is not in their schema at all.
      error => ({ kind: 'failed' as const, error }),
    );

  try {
    const outcome = await Promise.race([
      query,
      new Promise<{ kind: 'timeout' }>(resolve => {
        timer = setTimeout(() => resolve({ kind: 'timeout' }), timeoutMs);
      }),
    ]);

    if (outcome.kind === 'timeout') {
      console.warn('[closeActiveWpoVideoRequest] timed out; the WPO request may still be open');
      return;
    }
    if (outcome.kind === 'failed') {
      console.log('[closeActiveWpoVideoRequest] skipped', outcome.error);
      return;
    }

    const data = outcome.response.data?.closeActiveWpoVideoRequest;
    if (data && !data.closed && data.reason !== 'NOTHING_OPEN' && data.reason !== 'NO_PASSCODE') {
      console.warn(
        `[closeActiveWpoVideoRequest] not closed (${data.reason}); ` +
        `WPO request ${data.requestId} stays open and will block the next call`
      );
    }
  } finally {
    // Otherwise a pending timer keeps the logout path alive for up to timeoutMs after
    // the query already answered.
    if (timer) {
      clearTimeout(timer);
    }
  }
};
